import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faFaceGrin, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';

import Tooltip from 'Components/Molecules/Tooltip';
import TrustpilotStrip from 'Components/Usp/TrustpilotStrip/TrustpilotStrip';
import useViewport from 'Hooks/useViewport';
import 'Components/Usp/SmallUspBar.scss';


const messages = defineMessages({
  downloadandkeepforever: {
    id: 'small-usp-bar.point.download',
    defaultMessage: 'Download this Product & Keep Forever',
  },
  safeandsecure: {
    id: 'small-usp-bar.point.safeandsecure',
    defaultMessage: '100% Safe and Secure',
  },
  officalkeysonly: {
    id: 'smalluspbar.official-keys',
    defaultMessage: '100% Official Keys Only',
  },
});

export default function SmallUspBar({ product = {} }) {
  const { width, isBelowLg } = useViewport();
  const { formatMessage } = useIntl();
  const [slideNumber, setSlideNumber] = useState(1);
  const siteBrand = useSelector(state => state.siteBrand);
  const displayType = product?.display_type || 'game';

  const drm = product?.drm
    ? Object.keys(product.drm).filter(key => product.drm[key])
    : [];

  const comic = product?.bundles?.some(tier =>
    tier.games.some(game => game.type === 'comic' || game.type === 'book'),
  ) ?? false;


  const shouldShowUspBar = () => (
    window.locale === 'en' &&
    siteBrand === 'fanatical' &&
    drm[0] &&
    drm[0] !== 'origin' &&
    ['game', 'dlc', 'bundle'].includes(displayType)
  );

  useEffect(() => {
    let timer;
    if (isBelowLg) {
      timer = setTimeout(() => {
        if (slideNumber === 1) setSlideNumber(2);
        if (slideNumber === 2) setSlideNumber(3);
        if (slideNumber === 3) setSlideNumber(1);
      }, 5000);
    }

    // Clear timeout if the component is unmounted
    return () => clearTimeout(timer);
  }, [setSlideNumber, slideNumber, isBelowLg]);

  let productDrm = '';
  if (drm) {
    productDrm = drm[0];
  }
  let bannerType = 'keys';
  if (productDrm === 'uplay') bannerType = 'keyless';
  if (displayType === 'book-bundle' || productDrm === 'drm_free') bannerType = 'drm-free';

  const getComponentOneMessageData = () => {
    let ariaLabel = '';
    let message = '';

    switch (bannerType) {
      case 'drm-free':
        ariaLabel = 'Download this Product & Keep Forever, dropdown. expand for more details';
        message = messages.downloadandkeepforever;
        break;
      case 'keyless':
        ariaLabel = '100% Safe and Secure, dropdown. expand for more details';
        message = messages.safeandsecure;
        break;
      default:
        ariaLabel = '100% Official Keys Only, dropdown. expand for more details';
        message = messages.officalkeysonly;
    }

    return { ariaLabel, message };
  };

  const selectMessage = () => {
    switch (bannerType) {
      case 'drm-free':
        return (
          null
        );
      default:
        return (
          <FormattedMessage
            id="smalluspbar.popout"
            defaultMessage="All payments encrypted. {br}{br} Dedicated in-house customer support team."
            values={{ br: <br /> }}
          />
        );
    }
  };

  /* eslint-disable arrow-body-style */
  const componentOne = () => {
    const { ariaLabel, message } = getComponentOneMessageData();
    return (
      <div>
        <Tooltip
          ariaLabel={ariaLabel}
          hideTooltip={displayType === 'book-bundle' || displayType === 'comic-bundle' || bannerType === 'drm-free'}
          content={
            selectMessage()
          }
        >
          <div className="d-flex align-items-center align-self-center text-center">
            <div className="pr-2">
              <FontAwesomeIcon icon={faLock} />
            </div>

            {formatMessage(message)}

            {!isBelowLg && displayType !== 'book-bundle' && displayType !== 'comic-bundle' && bannerType !== 'drm-free' &&
              <div className="d-flex align-items-center pl-1">
                <FontAwesomeIcon icon={faChevronDown} className="small-chevron" />
              </div>
            }
          </div>
        </Tooltip>
      </div>
    );
  };

  const componentTwo = () => {
    return (
      <div>
        <TrustpilotStrip minimal={isBelowLg || width < 1025} logoColor="black" />
      </div>
    );
  };

  const componentThree = () => {
    return (
      <div>
        {comic && bannerType === 'drm-free' && (displayType === 'book-bundle' || displayType === 'comic-bundle') &&
          <Tooltip
            ariaLabel="14 Day Money Back Guarantee on this Product. Expand for more details"
            content={
              <FormattedMessage
                id="small-usp-bar.point.book-bundle"
                defaultMessage="We'll promptly refund any order within 14 days of purchase (provided the products or files haven't been accessed during this time)."
              />
            }
          >
            <div className={`d-flex align-items-center align-self-center text-center ${isBelowLg ? 'mobile-sizing' : ''}`}>
              <div className="pr-2">
                <FontAwesomeIcon icon={faFaceGrin} />
              </div>

              <FormattedMessage
                id="small-usp-bar.point.guarantee"
                defaultMessage="14 Day Money Back Guarantee on this Product"
              />

              {!isBelowLg &&
                <div className="d-flex align-items-center pl-1">
                  <FontAwesomeIcon icon={faChevronDown} className="small-chevron" />
                </div>
              }
            </div>
          </Tooltip>
        }
        {!comic &&
          <Tooltip
            ariaLabel="14 Day Money Back Guarantee on this Product. Expand for more details"
            content={
              <FormattedMessage
                id="small-usp-bar.point.guarantee.tooptip.books"
                defaultMessage="We'll promptly refund any order within 14 days of purchase (provided the products haven't been revealed)."
              />
            }
          >
            <div className="d-flex align-items-center align-self-center text-center">
              <div className="pr-2">
                <FontAwesomeIcon icon={faFaceGrin} />
              </div>

              <FormattedMessage
                id="small-usp-bar.official-safe"
                defaultMessage="14 Day Money Back Guarantee on this Product"
              />

              {!isBelowLg && bannerType !== 'drm-free' &&
                <div className="d-flex align-items-center pl-1">
                  <FontAwesomeIcon icon={faChevronDown} className="small-chevron" />
                </div>
              }
            </div>
          </Tooltip>
        }
      </div>
    );
  };

  const showUspBar = shouldShowUspBar();
  if (!showUspBar) return false;

  if (isBelowLg) {
    return (
      <div className="small-usp-bar product-usp-bar MobileUspBar">
        <div className={classNames('MobileUspBar__message MobileUspBar__message--one', { 'MobileUspBar__message--visible': slideNumber === 1 })}>
          <div className="MobileUspBar__message__content">
            <FontAwesomeIcon icon={faLock} />
            {bannerType === 'drm-free' &&
              formatMessage(messages.downloadandkeepforever)
            }
            {bannerType === 'keyless' &&
              formatMessage(messages.safeandsecure)
            }
            {bannerType === 'keys' &&
              formatMessage(messages.officalkeysonly)
            }
          </div>
        </div>

        <div className={classNames('MobileUspBar__message MobileUspBar__message--two', { 'MobileUspBar__message--visible': slideNumber === 2 })}>
          <TrustpilotStrip minimal={isBelowLg} logoColor="black" />
        </div>

        <div className={classNames('MobileUspBar__message MobileUspBar__message--three', { 'MobileUspBar__message--visible': slideNumber === 3 })}>
          <div className="MobileUspBar__message__content">
            <FontAwesomeIcon icon={faFaceGrin} />
            <FormattedMessage
              id="small-usp-bar.point.guarantee"
              defaultMessage="14 Day Money Back Guarantee on this Product"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div data-nosnippet className="small-usp-bar product-usp-bar">
        <div className="container d-flex justify-content-between align-items-center">
          <div className="mr-auto usp-distance">
            {componentOne()}
          </div>
          <div>
            {componentTwo()}
          </div>
          <div className="ml-auto">
            {componentThree()}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

SmallUspBar.propTypes = {
  product: PropTypes.shape({
    drm: PropTypes.object,
    display_type: PropTypes.string,
  }).isRequired,
};
